<template>
  <v-menu v-model="menuOpen" :close-on-content-click="false" location="bottom start" offset="4">
    <template #activator="{ props: menuProps }">
      <v-badge :model-value="activeCount > 0" :content="activeCount" color="primary" offset-x="4" offset-y="4">
        <v-btn v-bind="menuProps" variant="tonal" color="primary" size="small" prepend-icon="fa-filter">
          Élèves
        </v-btn>
      </v-badge>
    </template>

    <v-card min-width="360" max-width="420">
      <v-card-text class="pb-2" @keydown.enter="apply">
        <v-text-field v-model="draft.nameContains" label="Nom contient" density="compact" hide-details
          clearable class="mb-3" />
        <v-text-field v-model="draft.emailContains" label="Courriel contient" density="compact" hide-details
          clearable class="mb-3" />
        <v-text-field v-model="draft.recordNumberContains" label="Numéro de fiche contient" density="compact"
          hide-details clearable class="mb-3" />

        <div class="d-flex ga-2 mb-3">
          <v-text-field v-model.number="draft.ageMin" label="Âge de" type="number" min="0" density="compact"
            hide-details clearable />
          <v-text-field v-model.number="draft.ageMax" label="Âge à" type="number" min="0" density="compact"
            hide-details clearable />
        </div>

        <DateField v-model="birthDateModel" label="Date de naissance (exacte)" density="compact" hide-details
          clearable class="mb-3" />

        <v-autocomplete v-model="draft.pathTypes" :items="pathTypeOptions" item-title="label" item-value="value"
          label="Type de parcours" density="compact" hide-details multiple chips closable-chips clearable
          class="mb-3" />
        <v-autocomplete v-model="draft.exemptionCodes" :items="exemptionCodeOptions" item-title="label"
          item-value="value" label="Code d'exemption" density="compact" hide-details multiple chips closable-chips
          clearable class="mb-3" />
        <v-autocomplete v-model="draft.fundingSources" :items="fundingSourceOptions" item-title="label"
          item-value="value" label="Source de financement" density="compact" hide-details multiple chips
          closable-chips clearable />
      </v-card-text>
      <v-card-actions>
        <v-btn variant="text" size="small" @click="reset">Réinitialiser</v-btn>
        <v-spacer />
        <v-btn variant="text" size="small" @click="menuOpen = false">Annuler</v-btn>
        <v-btn color="primary" size="small" @click="apply">Appliquer</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import DateField from '@/components/date-field.vue';
import { fundingSourceOptions, pathTypeOptions, exemptionCodeOptions } from '@/utils/frequentation-codes';
import {
  type StudentFilterCriteria,
  emptyStudentFilter,
  normalizeStudentFilter,
  studentFilterCount,
} from '@/utils/student-filter';

const props = defineProps<{
  modelValue: StudentFilterCriteria;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: StudentFilterCriteria];
}>();

const menuOpen = ref(false);
const draft = ref<StudentFilterCriteria>(cloneFromModel());

const activeCount = computed(() => studentFilterCount(props.modelValue));

// DateField works with `string | null`; the criteria use `string | undefined`.
const birthDateModel = computed<string | null>({
  get: () => draft.value.birthDate ?? null,
  set: (v) => { draft.value.birthDate = v ?? undefined; },
});

function cloneFromModel(): StudentFilterCriteria {
  const m = props.modelValue ?? {};
  return {
    ...emptyStudentFilter(),
    ...m,
    // Copy arrays so edits to the draft don't mutate the committed value.
    pathTypes: [...(m.pathTypes ?? [])],
    exemptionCodes: [...(m.exemptionCodes ?? [])],
    fundingSources: [...(m.fundingSources ?? [])],
  };
}

// Refresh the draft from the committed value whenever the menu opens, so an
// abandoned edit (Annuler / click-away) is discarded.
watch(menuOpen, (open) => {
  if (open) draft.value = cloneFromModel();
});

function apply() {
  emit('update:modelValue', normalizeStudentFilter(draft.value) ?? emptyStudentFilter());
  menuOpen.value = false;
}

function reset() {
  draft.value = emptyStudentFilter();
  emit('update:modelValue', emptyStudentFilter());
  menuOpen.value = false;
}
</script>
